/**
 * Revent AI Refurbished Product Shopping Assistant
 * Storewide Multi-Touchpoint Discovery Concierge
 * Powered by the Revent backend with an on-device catalog fallback
 */

(function () {
  'use strict';

  const configEl = document.getElementById('revent-ai-config');
  
  const isArabic = document.documentElement.lang === 'ar' || document.documentElement.dir === 'rtl' || window.location.pathname.includes('/ar') || configEl?.dataset.locale === 'ar';
  const isKSA = window.location.hostname.includes('ksa.revent.store') || configEl?.dataset.market === 'KSA' || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active === 'SAR');
  const waNumber = isKSA ? '966544498604' : '971501407889';
  const currencyCode = configEl?.dataset.currency || window.Shopify?.currency?.active || 'AED';
  const currencySymbol = currencyCode === 'SAR' ? (isArabic ? 'ر.س' : 'SAR') : (isArabic ? 'د.إ' : 'AED');
  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));

  // Page Context
  const pageContext = {
    type: configEl?.dataset.pageType || 'general',
    product: configEl?.dataset.currentProductTitle ? {
      title: configEl.dataset.currentProductTitle,
      handle: configEl.dataset.currentProductHandle,
      price: parseFloat(configEl.dataset.currentProductPrice || '0'),
      type: configEl.dataset.currentProductType,
      vendor: configEl.dataset.currentProductVendor,
      tags: configEl.dataset.currentProductTags
    } : null,
    collection: configEl?.dataset.currentCollectionTitle || null
  };

  const I18N = {
    en: {
      botName: "Revent AI",
      onlineStatus: "Shopping guide",
      launcherText: "Find Your Tech with AI",
      proactiveTitle: "Personal Tech Advisor",
      proactiveText: pageContext.product 
        ? `Have questions about ${pageContext.product.title}? Ask me about specifications, condition, warranty, or alternatives.`
        : "Looking for an iPhone, MacBook, or iPad? Ask me any question, compare models, or tell me your budget.",
      proactiveCta: "Chat with AI ✨",
      inputPlaceholder: "Ask about a device or budget",
      welcomeMsg: pageContext.product
        ? `Looking at **${pageContext.product.title}**? Ask about its details or compare it with another available device.`
        : `Tell me what you need and your budget. I can help you compare the devices currently available at Revent.`,
      categories: pageContext.product ? [
        { label: "Compare alternatives", value: "What similar devices are available?" },
        { label: "Battery and warranty", value: "What is the battery and warranty policy for this device?" },
      ] : [
        { label: `Phones under 1,500 ${currencyCode}`, value: `phone under 1500 ${currencyCode}` },
        { label: "Compare devices", value: "Help me compare available devices" },
      ],
      viewingPill: pageContext.product ? `Viewing: ${pageContext.product.title}` : (pageContext.collection ? `Collection: ${pageContext.collection}` : null),
      thinkingMsg: "Looking through available devices...",
      viewProduct: "View Device",
      
      restartBtn: "Start over",
      closeBtn: "Close",
      addToCart: "Add to Cart",
      addingToCart: "Adding...",
      addedToCart: "Added! ✓",
      checkoutNow: "Checkout Now →",
      viewCart: "View Cart",
      cartBannerSub: "Certified Refurbished with 12M Warranty & 85%+ Battery",
      compareTitle: "Side-by-Side Comparison",
      compareSpecs: "Specs & Value Breakdown",
      helpful: "Helpful",
      notHelpful: "Not quite",
      copy: "Copy",
      copied: "Copied! ✓",
      micListening: "Listening... Speak your query 🎙️",
      toolWizard: "Help Me Choose",
      toolCompare: "Compare Devices",
      toolWarranty: "12M Warranty",
      toolBattery: "85%+ Battery",
      toolPayments: "Secure Payments",
      toolWhatsApp: "WhatsApp Specialist",
      toolCallback: "Request Callback",
      callUs: "Call Us"
    },
    ar: {
      botName: "ريفنت AI",
      onlineStatus: "دليل اختيار الأجهزة",
      launcherText: "اختر جهازك بالذكاء الاصطناعي",
      proactiveTitle: "مستشارك الشخصي للأجهزة",
      proactiveText: pageContext.product
        ? `هل لديك أي استفسار حول ${pageContext.product.title}؟ اسألني عن المواصفات والحالة والضمان أو البدائل.`
        : "تبحث عن آيفون، ماك بوك أو آيباد؟ اسألني أي سؤال، قارن بين الموديلات، أو حدد ميزانيتك واحتياجك.",
      proactiveCta: "تحدث مع المساعد الذكي ✨",
      inputPlaceholder: "اسأل عن جهاز أو ميزانيتك",
      welcomeMsg: pageContext.product
        ? `تتصفح **${pageContext.product.title}**؟ اسأل عن تفاصيله أو قارنه بجهاز آخر متوفر.`
        : `أخبرني بما تحتاجه وميزانيتك. يمكنني مساعدتك في مقارنة الأجهزة المتوفرة لدى ريفنت.`,
      categories: pageContext.product ? [
        { label: "قارن البدائل", value: "ما الأجهزة المشابهة المتوفرة؟" },
        { label: "البطارية والضمان", value: "ما سياسة البطارية والضمان لهذا الجهاز؟" },
      ] : [
        { label: `هواتف أقل من ١٥٠٠ ${currencySymbol}`, value: `هاتف أقل من 1500 ${currencySymbol}` },
        { label: "قارن الأجهزة", value: "ساعدني في مقارنة الأجهزة المتوفرة" },
      ],
      viewingPill: pageContext.product ? `تتصفح الآن: ${pageContext.product.title}` : (pageContext.collection ? `المجموعة: ${pageContext.collection}` : null),
      thinkingMsg: "أبحث في الأجهزة المتوفرة...",
      viewProduct: "عرض الجهاز",
      
      restartBtn: "البدء من جديد",
      closeBtn: "إغلاق",
      addToCart: "إضافة للسلة",
      addingToCart: "جاري الإضافة...",
      addedToCart: "تمت الإضافة! ✓",
      checkoutNow: "إتمام الشراء الآن ←",
      viewCart: "عرض السلة",
      cartBannerSub: "أجهزة أصلية معتمدة مع ضمان 12 شهراً وبطارية 85%+",
      compareTitle: "مقارنة المواصفات جنباً إلى جنب",
      compareSpecs: "مقارنة القيمة والمواصفات",
      helpful: "مفيد",
      notHelpful: "غير مناسب",
      copy: "نسخ",
      copied: "تم النسخ! ✓",
      micListening: "جاري الاستماع... تحدث الآن 🎙️",
      toolWizard: "ساعدني في الاختيار",
      toolCompare: "مقارنة الأجهزة",
      toolWarranty: "ضمان 12 شهراً",
      toolBattery: "بطارية 85%+",
      toolPayments: "طرق الدفع",
      toolWhatsApp: "استشارة واتساب",
      toolCallback: "طلب معاودة الاتصال",
      callUs: "اتصل بنا"
    }
  };

  const t = isArabic ? I18N.ar : I18N.en;

  // State & Chat Memory
  const state = {
    isOpen: false,
    previousFocus: null,
    previousOverflow: '',
    catalog: [],
    catalogLoaded: false,
    chatHistory: [],
    lastRecommendedProducts: []
  };

  // DOM Elements
  let launcherEl, bubbleEl, drawerEl, streamEl, inputFormEl, inputFieldEl;

  async function init() {
    createElements();
    attachListeners();
    bindGlobalTriggers();
  }

  // Load and normalize available storefront products when the assistant is used with rich variant & spec indexing
  async function loadCatalog() {
    if (state.catalogLoaded && state.catalog.length > 0) return state.catalog;

    // Check session storage cache for instant 24/7 offline catalog access
    try {
      const cached = sessionStorage.getItem('revent_ai_cached_catalog_v2');
      if (cached) {
        const parsedCache = JSON.parse(cached);
        if (Array.isArray(parsedCache) && parsedCache.length > 0 && parsedCache[0].variantId) {
          state.catalog = parsedCache;
          state.catalogLoaded = true;
        }
      }
    } catch (e) {}

    try {
      const res = await fetch('/collections/all/products.json?limit=250');
      const data = await res.json();
      if (data && data.products) {
        state.catalog = data.products.map(p => {
          const inStockVariants = (p.variants || []).filter(v => v.available).map(v => ({
            id: v.id,
            title: v.title,
            price: parseFloat(v.price || '0'),
            compare_at_price: parseFloat(v.compare_at_price || '0')
          }));

          const firstVariant = p.variants && p.variants[0] ? p.variants[0] : {};
          const isAvailable = inStockVariants.length > 0;
          const minPrice = inStockVariants.length > 0 ? Math.min(...inStockVariants.map(v => v.price)) : parseFloat(firstVariant.price || '0');
          const maxPrice = inStockVariants.length > 0 ? Math.max(...inStockVariants.map(v => v.price)) : minPrice;
          const imageSrc = p.images && p.images[0] ? p.images[0].src : '';
          
          const tLower = (p.title + ' ' + (p.product_type || '') + ' ' + (p.tags || []).join(' ')).toLowerCase();
          let category = 'other';
          if (tLower.includes('phone') || tLower.includes('iphone') || tLower.includes('galaxy') || tLower.includes('pixel') || tLower.includes('samsung')) {
            category = 'phone';
          } else if (tLower.includes('macbook') || tLower.includes('laptop') || tLower.includes('elitebook') || tLower.includes('thinkpad') || tLower.includes('dell')) {
            category = 'laptop';
          } else if (tLower.includes('ipad') || tLower.includes('tablet')) {
            category = 'tablet';
          } else if (tLower.includes('watch')) {
            category = 'watch';
          }

          const variantDetails = inStockVariants.map(v => `${v.title} (${v.price} ${currencyCode})`).slice(0, 6);
          const defaultVariantId = inStockVariants.length > 0 ? inStockVariants[0].id : (firstVariant.id || (p.variants && p.variants[0] ? p.variants[0].id : p.id));
          const comparePrice = inStockVariants.length > 0 && inStockVariants[0].compare_at_price ? inStockVariants[0].compare_at_price : (parseFloat(firstVariant.compare_at_price || '0') || null);

          return {
            id: p.id,
            variantId: defaultVariantId,
            title: p.title,
            handle: p.handle,
            url: `/products/${p.handle}`,
            price: minPrice,
            compare_at_price: comparePrice,
            max_price: maxPrice,
            available: isAvailable,
            variants: inStockVariants,
            in_stock_variants: variantDetails.join(', '),
            image: imageSrc,
            category: category,
            type: p.product_type || '',
            tags: p.tags || []
          };
        }).filter(p => p.available && p.price > 0);
        state.catalogLoaded = true;

        try {
          sessionStorage.setItem('revent_ai_cached_catalog_v2', JSON.stringify(state.catalog));
        } catch (e) {}
      }
    } catch (e) {
      console.warn('Revent AI: Error loading catalog JSON:', e);
    }
    return state.catalog;
  }

  // Markdown parser
  function parseMarkdown(mdText) {
    if (!mdText) return '';
    let html = mdText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    const lines = html.split('\n');
    let listType = null; // 'ul' or 'ol'
    let resultLines = [];

    for (let line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
        if (listType !== 'ul') {
          if (listType) resultLines.push(`</${listType}>`);
          resultLines.push('<ul>');
          listType = 'ul';
        }
        const itemText = trimmed.replace(/^[\*\-•]\s+/, '');
        resultLines.push(`<li>${itemText}</li>`);
      } else if (/^\d+\.\s/.test(trimmed)) {
        if (listType !== 'ol') {
          if (listType) resultLines.push(`</${listType}>`);
          resultLines.push('<ol>');
          listType = 'ol';
        }
        resultLines.push(`<li>${trimmed.replace(/^\d+\.\s+/, '')}</li>`);
      } else {
        if (listType) {
          resultLines.push(`</${listType}>`);
          listType = null;
        }
        if (trimmed.length > 0) {
          resultLines.push(`<p>${trimmed}</p>`);
        }
      }
    }
    if (listType) {
      resultLines.push(`</${listType}>`);
    }

    return resultLines.join('');
  }

  function createElements() {
    // 1. Floating Unified Concierge Launcher
    launcherEl = document.createElement('div');
    launcherEl.className = 'revent-ai-launcher';
    launcherEl.setAttribute('role', 'region');
    launcherEl.setAttribute('aria-label', t.botName);
    launcherEl.innerHTML = `
      <button type="button" class="revent-ai-launcher__main" aria-label="${t.launcherText}">
        <span class="revent-ai-launcher__icon">
          <svg viewBox="0 0 24 24"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
          <span class="revent-ai-launcher__pulse"></span>
        </span>
        <span class="revent-ai-launcher__text">${t.launcherText}</span>
      </button>
      <span class="revent-ai-launcher__divider" aria-hidden="true"></span>
      <button type="button" class="revent-ai-launcher__call" aria-label="${t.callUs}" title="${t.toolCallback}">
        <span class="revent-ai-launcher__call-icon">
          <svg viewBox="0 0 24 24"><path d="M7 3h3l1 5-2 1c1 3 3 5 6 6l1-2 5 1v3c0 2-2 4-4 4C9 20 4 15 3 7c0-2 2-4 4-4Z"/></svg>
        </span>
      </button>
    `;

    // 2. Proactive Bubble
    bubbleEl = document.createElement('div');
    bubbleEl.className = 'revent-ai-proactive-bubble' + (pageContext.product ? ' revent-ai-proactive-bubble--pdp' : '');
    bubbleEl.innerHTML = `
      <div class="revent-ai-proactive-bubble__header">
        <span class="revent-ai-proactive-bubble__badge">✨ ${t.proactiveTitle}</span>
        <button class="revent-ai-proactive-bubble__close" aria-label="${t.closeBtn}">✕</button>
      </div>
      <p class="revent-ai-proactive-bubble__text">${escapeHtml(t.proactiveText)}</p>
      ${pageContext.product ? `
        <div class="revent-ai-proactive-bubble__chips">
          <button type="button" class="revent-ai-proactive-chip" data-query="Tell me about the 85%+ battery guarantee and 12-month warranty on this device">🔋 ${isArabic ? 'ضمان البطارية 85%+ والـ 12 شهراً' : '85%+ Battery & 12M Warranty'}</button>
          <button type="button" class="revent-ai-proactive-chip" data-query="What are the best alternatives to this device in the same price range?">⚖️ ${isArabic ? 'مقارنة مع أفضل البدائل' : 'Compare Alternatives'}</button>
        </div>
      ` : ''}
      <button class="revent-ai-proactive-bubble__cta">${t.proactiveCta}</button>
    `;

    // 3. Conversation Drawer
    drawerEl = document.createElement('div');
    drawerEl.className = 'revent-ai-drawer';
    drawerEl.setAttribute('role', 'dialog');
    drawerEl.setAttribute('aria-label', t.botName);
    drawerEl.setAttribute('aria-modal', 'true');
    drawerEl.setAttribute('aria-hidden', 'true');
    drawerEl.inert = true;

    const viewingHeaderHtml = t.viewingPill ? `
      <div class="revent-ai-drawer__viewing-bar">
        <span>${escapeHtml(t.viewingPill)}</span>
      </div>
    ` : '';

    const backdropEl = document.createElement('div');
    backdropEl.className = 'revent-ai-backdrop';
    backdropEl.setAttribute('aria-hidden', 'true');
    drawerEl.innerHTML = `
      <div class="revent-ai-drawer__header">
        <div class="revent-ai-drawer__identity">
          <div class="revent-ai-drawer__avatar">
            <svg viewBox="0 0 24 24"><path d="M12 2a10 10 0 0 1 10 10c0 5.5-4.5 10-10 10S2 17.5 2 12 6.5 2 12 2zm0 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12z"/></svg>
          </div>
          <div class="revent-ai-drawer__info">
            <h2 class="revent-ai-drawer__title">${t.botName}</h2>
            <span>${t.onlineStatus}</span>
          </div>
        </div>
        <div class="revent-ai-drawer__actions">
          <button type="button" class="revent-ai-drawer__btn revent-ai-drawer__restart" title="${t.restartBtn}" aria-label="${t.restartBtn}">↺</button>
          <button type="button" class="revent-ai-drawer__btn revent-ai-drawer__close" title="${t.closeBtn}" aria-label="${t.closeBtn}">×</button>
        </div>
      </div>
      <nav class="revent-ai-drawer__quick-tools" id="reventAiQuickTools" aria-label="${isArabic ? 'خيارات المساعدة' : 'Shopping tools'}">
        <button type="button" class="revent-ai-tool-chip" data-tool="wizard">${t.toolWizard}</button>
        <button type="button" class="revent-ai-tool-chip" data-tool="compare">${t.toolCompare}</button>
        <button type="button" class="revent-ai-tool-chip" data-tool="warranty">${t.toolWarranty}</button>
      </nav>
      ${viewingHeaderHtml}
      <div class="revent-ai-stream" id="reventAiStream" role="log" aria-live="polite" aria-relevant="additions"></div>
      <div class="revent-ai-drawer__footer">
        <form class="revent-ai-input-form" id="reventAiForm">
          <input type="text" placeholder="${t.inputPlaceholder}" id="reventAiInput" autocomplete="off" aria-label="${t.inputPlaceholder}" />
          <button type="button" class="revent-ai-mic-btn" id="reventAiMic" title="${isArabic ? 'البحث الصوتي' : 'Voice search'}" aria-label="${isArabic ? 'البحث الصوتي' : 'Voice search'}">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
            </svg>
          </button>
          <button type="submit" aria-label="${isArabic ? 'إرسال الرسالة' : 'Send message'}">
            <svg viewBox="0 0 24 24"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
          </button>
        </form>
        <p class="revent-ai-disclosure">${isArabic ? 'اقتراحات آلية. تحقق من التفاصيل في صفحة الجهاز.' : 'AI suggestions. Confirm details on the product page.'} <a href="https://wa.me/${waNumber}" target="_blank" rel="noopener noreferrer">${isArabic ? 'تحدث مع شخص' : 'Talk to a person'}</a></p>
      </div>
    `;

    document.body.appendChild(launcherEl);
    document.body.appendChild(bubbleEl);
    document.body.appendChild(backdropEl);
    document.body.appendChild(drawerEl);
    backdropEl.addEventListener('click', closeDrawer);

    streamEl = drawerEl.querySelector('#reventAiStream');
    inputFormEl = drawerEl.querySelector('#reventAiForm');
    inputFieldEl = drawerEl.querySelector('#reventAiInput');
  }

  function openCallbackDialog() {
    const dialog = document.getElementById('CodexCallbackDialog');
    const overlay = document.querySelector('[data-codex-callback-overlay]');
    if (dialog && overlay) {
      dialog.hidden = false;
      overlay.hidden = false;
      document.body.classList.add('codex-callback-open');
      const input = document.getElementById('CodexCallbackPhone');
      if (input) input.focus();
    } else {
      const trigger = document.querySelector('.codex-callback-trigger');
      if (trigger) trigger.click();
    }
  }

  function attachListeners() {
    const mainBtn = launcherEl.querySelector('.revent-ai-launcher__main');
    if (mainBtn) {
      mainBtn.addEventListener('click', () => openDrawer());
    }
    const callBtn = launcherEl.querySelector('.revent-ai-launcher__call');
    if (callBtn) {
      callBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        openCallbackDialog();
      });
    }
    bubbleEl.querySelector('.revent-ai-proactive-bubble__cta').addEventListener('click', () => openDrawer());
    bubbleEl.querySelector('.revent-ai-proactive-bubble__close').addEventListener('click', () => {
      bubbleEl.classList.remove('is-visible');
      try {
        sessionStorage.setItem('revent_ai_bubble_dismissed', '1');
      } catch (e) {}
    });

    bubbleEl.querySelectorAll('.revent-ai-proactive-chip').forEach(chip => {
      chip.addEventListener('click', (e) => {
        e.stopPropagation();
        const q = chip.getAttribute('data-query');
        bubbleEl.classList.remove('is-visible');
        try {
          sessionStorage.setItem('revent_ai_bubble_dismissed', '1');
        } catch (err) {}
        openDrawer(q);
      });
    });

    drawerEl.querySelector('.revent-ai-drawer__close').addEventListener('click', closeDrawer);
    drawerEl.querySelector('.revent-ai-drawer__restart').addEventListener('click', restartConversation);
    drawerEl.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); closeDrawer(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...drawerEl.querySelectorAll('button:not([disabled]), a[href], input:not([disabled])')].filter(el => el.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });

    inputFormEl.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = inputFieldEl.value.trim();
      if (!text) return;
      handleUserMessage(text);
      inputFieldEl.value = '';
    });

    attachQuickToolListeners();
    setupVoiceSearch();
  }

  // Bind all in-page custom triggers (Product page, Search, Collections)
  function bindGlobalTriggers() {
    document.addEventListener('click', (e) => {
      const triggerBtn = e.target.closest('[data-revent-ai-query], [data-revent-ai-trigger]');
      if (triggerBtn) {
        e.preventDefault();
        const query = triggerBtn.dataset.reventAiQuery || triggerBtn.dataset.aiPrompt;
        openDrawer(query);
      }
    });

    // Expose global API
    window.openReventAi = function (initialQuery) {
      openDrawer(initialQuery);
    };
  }

  function scheduleProactiveBubble() {
    try {
      if (sessionStorage.getItem('revent_ai_bubble_dismissed')) return;
    } catch (e) {}

    // Suppress proactive bubble on form-entry and utility pages to prevent obscuring critical inputs
    const pathname = window.location.pathname.toLowerCase();
    const isFormOrUtilityPage = (
      pathname.includes('/pages/contact') ||
      pathname.includes('/account') ||
      pathname.includes('/cart') ||
      pathname.includes('/checkout') ||
      document.querySelector('form.contact-form') !== null ||
      document.querySelector('form[action*="/contact"]') !== null ||
      document.querySelector('.customer-login') !== null
    );
    if (isFormOrUtilityPage) return;

    // If cookie consent is currently visible and not accepted, do not show proactive bubble over it
    const cookieConsentEl = document.querySelector('cookie-consent');
    const isCookieBannerVisible = cookieConsentEl && 
      cookieConsentEl.style.display !== 'none' && 
      cookieConsentEl.offsetHeight > 0;
    if (isCookieBannerVisible) return;

    let triggered = false;
    const showBubble = () => {
      if (triggered || state.isOpen) return;
      try {
        if (sessionStorage.getItem('revent_ai_bubble_dismissed')) return;
      } catch (e) {}

      const stillHasCookie = document.querySelector('cookie-consent') && 
        document.querySelector('cookie-consent').offsetHeight > 0;
      if (stillHasCookie) return;

      triggered = true;
      bubbleEl.classList.add('is-visible');

      // Auto-dismiss after 15 seconds if not interacted with to preserve viewport usability
      setTimeout(() => {
        if (bubbleEl && bubbleEl.classList.contains('is-visible') && !state.isOpen) {
          bubbleEl.classList.remove('is-visible');
        }
      }, 15000);
    };

    // 1. Time-based linger: 8.5s on PDP, 5s on other pages
    const delay = pageContext.product ? 8500 : 5000;
    const timer = setTimeout(showBubble, delay);

    // 2. Scroll-based trigger on PDP: show when customer scrolls past hero image into specs
    if (pageContext.product) {
      const onScroll = () => {
        if (window.scrollY > 300) {
          clearTimeout(timer);
          window.removeEventListener('scroll', onScroll);
          showBubble();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  function openDrawer(initialQuery = null) {
    if (!state.isOpen) {
      state.previousFocus = document.activeElement;
      state.previousOverflow = document.body.style.overflow;
    }
    state.isOpen = true;
    drawerEl.inert = false;
    drawerEl.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    drawerEl.classList.add('is-open');
    document.querySelector('.revent-ai-backdrop')?.classList.add('is-visible');
    drawerEl.querySelector('.revent-ai-drawer__close')?.focus();
    bubbleEl.classList.remove('is-visible');
    
    if (streamEl.children.length === 0) {
      renderWelcomeStep();
    }

    if (initialQuery && typeof initialQuery === 'string' && initialQuery.trim().length > 0) {
      setTimeout(() => {
        handleUserMessage(initialQuery.trim());
      }, 300);
    }
  }

  function closeDrawer() {
    state.isOpen = false;
    drawerEl.classList.remove('is-open');
    drawerEl.setAttribute('aria-hidden', 'true');
    drawerEl.inert = true;
    document.querySelector('.revent-ai-backdrop')?.classList.remove('is-visible');
    document.body.style.overflow = state.previousOverflow;
    if (state.previousFocus?.isConnected) state.previousFocus.focus();
  }

  function restartConversation() {
    state.chatHistory = [];
    streamEl.innerHTML = '';
    renderWelcomeStep();
  }

  function addBotMessage(text, options = null, cards = null) {
    const msg = document.createElement('div');
    msg.className = 'revent-ai-msg revent-ai-msg--bot';
    const isWelcome = options === t.categories && !cards;
    if (isWelcome) msg.classList.add('revent-ai-msg--welcome');
    
    let optionsHtml = '';
    if (options && options.length > 0) {
      optionsHtml = `
        <div class="revent-ai-options">
          ${options.map(opt => {
            const val = opt.value || opt.label;
            const label = String(opt.label || '').replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/u, '').trim();
            return `<button type="button" class="revent-ai-pill" data-value="${escapeHtml(val)}"><span>${escapeHtml(label)}</span></button>`;
          }).join('')}
        </div>
      `;
    }

    let cardsHtml = '';
    if (cards && cards.length > 0) {
      state.lastRecommendedProducts = cards;
      cardsHtml = `
        <div class="revent-ai-products-grid">
          ${cards.map(prod => {
            const formattedPrice = `${prod.max_price > prod.price ? (isArabic ? 'ابتداءً من ' : 'From ') : ''}${prod.price.toLocaleString()} ${currencySymbol}`;
            const variantId = prod.variantId || (prod.variants && prod.variants[0] ? prod.variants[0].id : '');
            const badgeText = isArabic ? 'راجع حالة الجهاز والضمان' : 'Review condition and warranty';


            return `
              <div class="revent-ai-product-card" data-handle="${escapeHtml(prod.handle)}">
                <a class="revent-ai-product-card__thumb-link" href="${escapeHtml(prod.url)}">
                  <div class="revent-ai-product-card__thumb">
                    <img src="${escapeHtml(prod.image)}" alt="${escapeHtml(prod.title)}" loading="lazy" />
                    
                  </div>
                </a>
                <div class="revent-ai-product-card__details">
                  <a class="revent-ai-product-card__title-link" href="${escapeHtml(prod.url)}">
                    <h5 class="revent-ai-product-card__title">${escapeHtml(prod.title)}</h5>
                  </a>
                  <div class="revent-ai-product-card__price-row">
                    <span class="revent-ai-product-card__price">${formattedPrice}</span>

                  </div>

                  <div class="revent-ai-product-card__badges">
                    <span class="revent-ai-product-card__badge">${badgeText}</span>
                  </div>
                  <div class="revent-ai-product-card__action">
                    <a class="revent-ai-product-card__btn" href="${escapeHtml(prod.url)}"><span>${t.viewProduct}</span> <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg></a>
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      `;
    }

    const formattedContent = parseMarkdown(text);

    msg.innerHTML = `
      <div class="revent-ai-msg__badge">${isArabic ? 'دليل ريفنت' : 'REVENT GUIDE'}</div>
      <div class="revent-ai-msg__content">${formattedContent}</div>
      ${cardsHtml}
      ${optionsHtml}
    `;

    streamEl.appendChild(msg);
    streamEl.scrollTop = streamEl.scrollHeight;

    // Attach pill click listeners
    if (options && options.length > 0) {
      const pills = msg.querySelectorAll('.revent-ai-pill');
      pills.forEach(pill => {
        pill.addEventListener('click', () => {
          handleUserMessage(pill.dataset.value || pill.textContent.trim());
        });
      });
    }


  }

  // Interactive Device Matcher Wizard ("Help Me Choose")
  function renderWizardCard(step = 1, currentChoices = {}) {
    const wizardEl = document.createElement('div');
    wizardEl.className = 'revent-ai-msg revent-ai-msg--bot';

    let stepTitle = '';
    let questionText = '';
    let options = [];
    let progressPct = 33;

    if (step === 1) {
      stepTitle = isArabic ? 'الخطوة 1 من 3: نوع الجهاز' : 'Step 1 of 3: Device Type';
      questionText = isArabic ? 'ما هو نوع الجهاز الذي تبحث عنه؟' : 'What type of device are you looking for?';
      progressPct = 33;
      options = [
        { label: isArabic ? '📱 آيفون / هاتف ذكي' : '📱 iPhone / Smartphone', value: 'phone' },
        { label: isArabic ? '💻 ماك بوك / لابتوب' : '💻 MacBook / Laptop', value: 'laptop' },
        { label: isArabic ? '📱 آيباد / تابلت' : '📱 iPad / Tablet', value: 'tablet' },
        { label: isArabic ? '⌚ ساعة ذكية' : '⌚ Apple Watch / Smartwatch', value: 'watch' }
      ];
    } else if (step === 2) {
      stepTitle = isArabic ? 'الخطوة 2 من 3: أولويتك الأساسية' : 'Step 2 of 3: Primary Priority';
      questionText = isArabic ? 'ما هو الأهم بالنسبة لك في استخدامك؟' : 'What matters most for your daily use?';
      progressPct = 66;
      const devType = currentChoices.device || 'phone';
      if (devType === 'laptop') {
        options = [
          { label: isArabic ? '🎓 دراسة وجامعة وحمل خفيف' : '🎓 College & Portable Study', value: 'study' },
          { label: isArabic ? '💼 عمل مكتبي وبرامج وتعدد مهام' : '💼 Business & Multitasking', value: 'business' },
          { label: isArabic ? '🎨 مونتاج وتصميم وبرمجة (M1/M2)' : '🎨 Creative, Design & Code', value: 'pro' },
          { label: isArabic ? '💰 أفضل قيمة اقتصادية' : '💰 Best Budget Value', value: 'budget' }
        ];
      } else if (devType === 'tablet') {
        options = [
          { label: isArabic ? '✏️ دراسة وتدوين ورسم' : '✏️ Notes, Study & Pencil', value: 'notes' },
          { label: isArabic ? '🎬 مسلسلات وأفلام وترفيه' : '🎬 Movies & Streaming', value: 'media' },
          { label: isArabic ? '👶 أطفال وتطبيقات عائلية' : '👶 Kids & Family Apps', value: 'kids' },
          { label: isArabic ? '💰 شاشة كبيرة بسعر اقتصادي' : '💰 Big Screen on Budget', value: 'budget' }
        ];
      } else {
        options = [
          { label: isArabic ? '📸 كاميرا احترافية وتصوير 4K' : '📸 Pro Cameras & 4K Video', value: 'camera' },
          { label: isArabic ? '🔋 بطارية عملاقة تدوم طويلاً' : '🔋 Heavy Battery Life', value: 'battery' },
          { label: isArabic ? '⚡ أداء صاروخي وألعاب' : '⚡ High Performance & Gaming', value: 'gaming' },
          { label: isArabic ? '💰 قيمة ممتازة واستخدام يومي' : '💰 Best Value Everyday Driver', value: 'daily' }
        ];
      }
    } else if (step === 3) {
      stepTitle = isArabic ? 'الخطوة 3 من 3: الميزانية المناسبة' : 'Step 3 of 3: Target Budget';
      questionText = isArabic ? 'ما هو نطاق الميزانية المفضل لديك؟' : 'What is your preferred budget range?';
      progressPct = 100;
      options = [
        { label: `< 1,000 ${currencySymbol}`, value: 'under 1000' },
        { label: `1,000 – 2,000 ${currencySymbol}`, value: 'between 1000 and 2000' },
        { label: `2,000 – 3,500 ${currencySymbol}`, value: 'between 2000 and 3500' },
        { label: `3,500+ ${currencySymbol}`, value: 'over 3500' },
        { label: isArabic ? '✨ أي ميزانية (أفضل المواصفات)' : '✨ Any Budget (Best Specs)', value: 'any budget' }
      ];
    }

    wizardEl.innerHTML = `
      <div class="revent-ai-msg__badge">✨ ${isArabic ? 'مستشار ريفنت الذكي' : 'Revent AI Advisor'}</div>
      <div class="revent-ai-wizard">
        <div class="revent-ai-wizard__header">
          <span class="revent-ai-wizard__title">🎯 ${isArabic ? 'مساعد الاختيار الذكي' : 'Interactive Device Matcher'}</span>
          <span class="revent-ai-wizard__step-badge">${stepTitle}</span>
        </div>
        <div class="revent-ai-wizard__progress">
          <div class="revent-ai-wizard__progress-bar" style="width: ${progressPct}%"></div>
        </div>
        <p class="revent-ai-wizard__question">${questionText}</p>
        <div class="revent-ai-wizard__options">
          ${options.map(opt => `
            <button class="revent-ai-wizard__btn" data-val="${opt.value}">${opt.label}</button>
          `).join('')}
        </div>
      </div>
    `;

    streamEl.appendChild(wizardEl);
    streamEl.scrollTop = streamEl.scrollHeight;

    const btns = wizardEl.querySelectorAll('.revent-ai-wizard__btn');
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        const val = btn.dataset.val;
        btn.classList.add('is-selected');
        btns.forEach(b => b.disabled = true);

        if (step === 1) {
          currentChoices.device = val;
          setTimeout(() => renderWizardCard(2, currentChoices), 250);
        } else if (step === 2) {
          currentChoices.priority = val;
          setTimeout(() => renderWizardCard(3, currentChoices), 250);
        } else if (step === 3) {
          currentChoices.budget = val;
          const synthesizedQuery = isArabic
            ? `أريد ${currentChoices.device} يركز على ${currentChoices.priority} بميزانية ${currentChoices.budget}`
            : `I want a ${currentChoices.device} for ${currentChoices.priority} with budget ${currentChoices.budget}`;
          setTimeout(() => handleUserMessage(synthesizedQuery), 350);
        }
      });
    });
  }

  // Interactive Side-by-Side Device Comparison
  function renderComparisonCard(prod1, prod2) {
    if (!prod1 || !prod2) return;
    const compEl = document.createElement('div');
    compEl.className = 'revent-ai-msg revent-ai-msg--bot';

    compEl.innerHTML = `
      <div class="revent-ai-msg__badge">${isArabic ? 'دليل ريفنت' : 'REVENT GUIDE'}</div>
      <div class="revent-ai-comparison">
        <div class="revent-ai-comparison__header">
          <h5>⚖️ ${t.compareTitle}</h5>
          <span>${t.compareSpecs}</span>
        </div>
        <div class="revent-ai-comparison__grid">
          <div class="revent-ai-comparison__col">
            <div class="revent-ai-comparison__thumb">
              <img src="${escapeHtml(prod1.image)}" alt="${escapeHtml(prod1.title)}" loading="lazy" />
            </div>
            <h6>${escapeHtml(prod1.title)}</h6>
            <div class="revent-ai-comparison__price">${prod1.price.toLocaleString()} ${currencySymbol}</div>
            
            <div class="revent-ai-comparison__actions">
              <a class="revent-ai-product-card__btn" href="${escapeHtml(prod1.url)}"><span>${t.viewProduct}</span></a>
            </div>
          </div>
          <div class="revent-ai-comparison__vs">VS</div>
          <div class="revent-ai-comparison__col">
            <div class="revent-ai-comparison__thumb">
              <img src="${escapeHtml(prod2.image)}" alt="${escapeHtml(prod2.title)}" loading="lazy" />
            </div>
            <h6>${escapeHtml(prod2.title)}</h6>
            <div class="revent-ai-comparison__price">${prod2.price.toLocaleString()} ${currencySymbol}</div>
            
            <div class="revent-ai-comparison__actions">
              <a class="revent-ai-product-card__btn" href="${escapeHtml(prod2.url)}"><span>${t.viewProduct}</span></a>
            </div>
          </div>
        </div>
      </div>
    `;

    streamEl.appendChild(compEl);
    streamEl.scrollTop = streamEl.scrollHeight;

  }

  function handleCompareDevices() {
    if (state.lastRecommendedProducts && state.lastRecommendedProducts.length >= 2) {
      renderComparisonCard(state.lastRecommendedProducts[0], state.lastRecommendedProducts[1]);
    } else if (state.catalog && state.catalog.length >= 2) {
      renderComparisonCard(state.catalog[0], state.catalog[1]);
    } else {
      handleUserMessage(isArabic ? 'قارن بين أفضل جهازين متوفرين' : 'Compare two popular devices in catalog');
    }
  }

  // Quick Tools Bar Listener
  function attachQuickToolListeners() {
    const toolsBar = drawerEl.querySelector('#reventAiQuickTools');
    if (!toolsBar) return;
    toolsBar.addEventListener('click', (e) => {
      const chip = e.target.closest('.revent-ai-tool-chip');
      if (!chip || chip.tagName.toLowerCase() === 'a') return;
      const tool = chip.dataset.tool;
      if (tool === 'wizard') {
        renderWizardCard(1);
      } else if (tool === 'compare') {
        handleCompareDevices();
      } else if (tool === 'warranty') {
        handleUserMessage(isArabic ? 'ما هي تفاصيل وسياسة الضمان؟' : 'Tell me about the warranty policy and coverage');
      } else if (tool === 'battery') {
        handleUserMessage(isArabic ? 'ما هو ضمان صحة البطارية للأجهزة؟' : 'What is the battery health guarantee on refurbished devices?');
      } else if (tool === 'installments' || tool === 'payments') {
        handleUserMessage(isArabic ? 'ما هي طرق الدفع المتاحة؟' : 'What payment methods do you accept?');
      } else if (tool === 'callback') {
        openCallbackDialog();
      }
    });
  }

  // Speech-to-Text Voice Input
  function setupVoiceSearch() {
    const micBtn = drawerEl.querySelector('#reventAiMic');
    if (!micBtn) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      micBtn.style.display = 'none';
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = isArabic ? (isKSA ? 'ar-SA' : 'ar-AE') : 'en-US';

    let isRecording = false;

    micBtn.addEventListener('click', () => {
      if (isRecording) {
        recognition.stop();
        return;
      }

      try {
        recognition.start();
        isRecording = true;
        micBtn.classList.add('is-listening');
        inputFieldEl.setAttribute('placeholder', t.micListening);
      } catch (e) {
        isRecording = false;
        micBtn.classList.remove('is-listening');
      }
    });

    recognition.onresult = (event) => {
      const transcript = event.results && event.results[0] && event.results[0][0] ? event.results[0][0].transcript : '';
      if (transcript && transcript.trim()) {
        inputFieldEl.value = transcript.trim();
        handleUserMessage(transcript.trim());
        inputFieldEl.value = '';
      }
    };

    recognition.onend = () => {
      isRecording = false;
      micBtn.classList.remove('is-listening');
      inputFieldEl.setAttribute('placeholder', t.inputPlaceholder);
    };

    recognition.onerror = () => {
      isRecording = false;
      micBtn.classList.remove('is-listening');
      inputFieldEl.setAttribute('placeholder', t.inputPlaceholder);
    };
  }

  function addUserMessage(text) {
    const msg = document.createElement('div');
    msg.className = 'revent-ai-msg revent-ai-msg--user';
    msg.innerHTML = `<div class="revent-ai-msg__content">${escapeHtml(text)}</div>`;
    streamEl.appendChild(msg);
    streamEl.scrollTop = streamEl.scrollHeight;
  }

  function showTypingIndicator() {
    const existing = document.getElementById('reventAiTyping');
    if (existing) existing.remove();

    const typing = document.createElement('div');
    typing.className = 'revent-ai-typing revent-ai-msg revent-ai-msg--bot';
    typing.id = 'reventAiTyping';
    
    const statusText = isArabic 
      ? 'لحظة من فضلك...' 
      : 'One moment...';

    typing.innerHTML = `
      <div class="revent-ai-typing__card">
        <div class="revent-ai-typing__avatar">✨</div>
        <div class="revent-ai-typing__content">
          <div class="revent-ai-typing__dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <span class="revent-ai-typing__status">${statusText}</span>
        </div>
      </div>
    `;
    streamEl.appendChild(typing);
    streamEl.scrollTop = streamEl.scrollHeight;

    // Update header status text to "Typing..."
    const headerStatus = drawerEl?.querySelector('.revent-ai-drawer__info span');
    if (headerStatus) {
      headerStatus.dataset.defaultText = headerStatus.textContent;
      headerStatus.innerHTML = `<span class="revent-ai-typing-dot">●</span> ${isArabic ? 'جاري الكتابة والتحليل...' : 'Thinking & Typing...'}`;
    }
  }

  function removeTypingIndicator() {
    const typing = document.getElementById('reventAiTyping');
    if (typing) typing.remove();

    // Restore header status text
    const headerStatus = drawerEl?.querySelector('.revent-ai-drawer__info span');
    if (headerStatus && headerStatus.dataset.defaultText) {
      headerStatus.textContent = headerStatus.dataset.defaultText;
    }
  }

  function renderWelcomeStep() {
    addBotMessage(t.welcomeMsg, t.categories);
  }

  // Parse Budget Constraints
  function parseBudgetConstraints(text) {
    const lower = text.toLowerCase().replace(/[٠-٩]/g, digit => String(digit.charCodeAt(0) - 0x0660)).replace(/[۰-۹]/g, digit => String(digit.charCodeAt(0) - 0x06F0));
    const rangeMatch = lower.match(/(?:between|from|من|بين)?\s*(\d{3,5})\s*(?:to|and|-|إلى|الي|و)\s*(\d{3,5})/i);
    if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
      const min = parseInt(rangeMatch[1], 10);
      const max = parseInt(rangeMatch[2], 10);
      return { min: Math.min(min, max), max: Math.max(min, max) };
    }

    const ceilingMatch = lower.match(/(?:under|below|less than|max|budget|within|up to|أقل من|بحدود|ميزانية|تحت)\s*(\d{3,5})/i);
    if (ceilingMatch && ceilingMatch[1]) {
      return { min: 0, max: parseInt(ceilingMatch[1], 10) };
    }

    const simpleMatch = lower.match(/(\d{3,5})\s*(?:aed|sar|dh|dhs|dirhams?|riyals?|درهم|ريال|د\.إ|ر\.س)/i);
    if (simpleMatch && simpleMatch[1]) {
      return { min: 0, max: parseInt(simpleMatch[1], 10) };
    }

    return { min: 0, max: 99999 };
  }

  // Parse Category Intent
  function parseCategoryIntent(text) {
    const lower = text.toLowerCase();
    if (lower.includes('phone') || lower.includes('mobile') || lower.includes('iphone') || lower.includes('galaxy') || lower.includes('جوال') || lower.includes('موبايل') || lower.includes('هاتف') || lower.includes('تلفون') || lower.includes('آيفون')) {
      return 'phone';
    }
    if (lower.includes('laptop') || lower.includes('macbook') || lower.includes('mac') || lower.includes('notebook') || lower.includes('elitebook') || lower.includes('thinkpad') || lower.includes('لابتوب') || lower.includes('ماك') || lower.includes('كمبيوتر')) {
      return 'laptop';
    }
    if (lower.includes('ipad') || lower.includes('tablet') || lower.includes('آيباد') || lower.includes('تابلت') || lower.includes('لوحي')) {
      return 'tablet';
    }
    if (lower.includes('watch') || lower.includes('ساعة')) {
      return 'watch';
    }
    return null;
  }

  // Ask the configured assistant service
  async function callBackendServer(userText) {
    const apiEndpoint = configEl?.dataset.apiEndpoint || window.REVENT_AI_API_ENDPOINT;
    if (!apiEndpoint) return null;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6500);

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Market": isKSA ? "KSA" : "UAE"
        },
        signal: controller.signal,
        body: JSON.stringify({
          message: userText,
          history: state.chatHistory.slice(-6),
          market: isKSA ? "KSA" : "UAE",
          currency: currencyCode,
          locale: isArabic ? "ar" : "en",
          pageContext: pageContext.product || null
        })
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        if (data && data.reply) {
          const handles = Array.isArray(data.products) && data.products.length > 0
            ? data.products.map(p => p.handle)
            : (data.recommended_handles || []);

          return {
            reply: data.reply,
            recommended_handles: handles,
            products: data.products || [],
            follow_up_pills: data.follow_up_pills || [],
            show_products: handles.length > 0
          };
        }
      }
    } catch (err) {
      // Backend server unavailable or timed out -> cascade to fallback
    }
    return null;
  }

  // =========================================================================
  // Local catalog and policy fallback
  // Ensures Revent AI never goes down even without API keys, credits, or internet
  // =========================================================================
  const FAQ_KNOWLEDGE_BASE = [
    {
      id: "warranty",
      keywords: ["warranty", "guarantee", "guaranty", "ضمان", "مضمون", "كفالة", "ضمانكم", "خراب", "تصليح", "عطل"],
      answer: {
        en: "**Warranty**\n\nRevent lists a 12-month warranty. Coverage and exclusions are in the warranty terms; check the product page before ordering.",
        ar: "**الضمان**\n\nتعرض ريفنت ضماناً لمدة 12 شهراً. راجع شروط الضمان وتفاصيل الجهاز لمعرفة التغطية والاستثناءات قبل الطلب."
      },
      pills: {
        en: ["🔋 Battery Health?", "💳 Payment Options", "📱 Browse Phones"],
        ar: ["🔋 صحة البطارية؟", "💳 طرق الدفع", "📱 تصفح الهواتف"]
      }
    },
    {
      id: "battery",
      keywords: ["battery", "battery health", "health", "mah", "charging", "بطارية", "شحن", "نسبة البطارية", "صحة البطارية", "كم بطاريت"],
      answer: {
        en: "**Battery condition**\n\nCheck the battery details on the individual device page. Revent advertises an 85%+ minimum for eligible phones; ask support if a listing is unclear.",
        ar: "**حالة البطارية**\n\nراجع تفاصيل البطارية في صفحة الجهاز. تعلن ريفنت حداً أدنى 85%+ للهواتف المؤهلة؛ اسأل الدعم إذا كانت بيانات جهاز معين غير واضحة."
      },
      pills: {
        en: ["🛡️ Warranty details", "📦 What's in the box?", "📱 Browse iPhones"],
        ar: ["🛡️ تفاصيل الضمان", "📦 محتويات العلبة", "📱 تصفح أجهزة آيفون"]
      }
    },
    {
      id: "refurbished_worth",
      keywords: ["refurbished worth", "is refurbished", "why refurbished", "used vs refurbished", "مجدد", "مستعمل", "هل المجدد", "يسوى", "يستحق", "الفرق بين المجدد"],
      answer: {
        en: "**Buying refurbished**\n\nCompare the device's listed condition, storage, battery information, warranty and current price. The right choice depends on your needs and the available unit.",
        ar: "**شراء جهاز مجدد**\n\nقارن حالة الجهاز المعروضة وسعته وبطاريته وضمانه وسعره الحالي. الاختيار المناسب يعتمد على احتياجك والجهاز المتوفر."
      },
      pills: {
        en: ["📱 Show Top Phones", "💻 Show MacBooks", "🔋 Battery Guarantee"],
        ar: ["📱 أفضل الهواتف", "💻 أجهزة ماك بوك", "🔋 ضمان البطارية"]
      }
    },
    {
      id: "payments",
      keywords: ["payment", "tabby", "tamara", "installment", "cod", "cash on delivery", "card", "apple pay", "mada", "stripe", "دفع", "تقسيط", "تابي", "تمارا", "كاش", "عند الاستلام", "اقساط", "فيزا", "مدى", "بطاقة"],
      answer: {
        en: "**Payment options**\n\nThe checkout shows the payment methods available for your location and device. Saudi orders currently display and charge in AED. Review the total before paying.",
        ar: "**طرق الدفع**\n\nتظهر طرق الدفع المتاحة لموقعك وجهازك عند إتمام الطلب. تُعرض وتُحصّل طلبات السعودية حالياً بالدرهم الإماراتي. راجع الإجمالي قبل الدفع."
      },
      pills: {
        en: ["🚚 Shipping & Delivery", "🛡️ Warranty Coverage", "📱 Mobile under 1500"],
        ar: ["🚚 الشحن والتوصيل", "🛡️ تغطية الضمان", "📱 جوال أقل من 1500"]
      }
    },
    {
      id: "returns",
      keywords: ["return", "returns", "refund", "exchange", "money back", "استرجاع", "ارجاع", "تبديل", "استبدال", "فلوسي", "تراجع"],
      answer: {
        en: "**Returns**\n\nRevent publishes a 7-calendar-day return policy. Check the return and refund terms for eligibility, condition requirements and the refund process.",
        ar: "**الإرجاع**\n\nتنشر ريفنت سياسة إرجاع خلال 7 أيام تقويمية. راجع شروط الإرجاع والاسترداد لمعرفة الأهلية وحالة الجهاز المطلوبة وخطوات الاسترداد."
      },
      pills: {
        en: ["🛡️ 12-Month Warranty details", "💳 Secure Payment Methods", "📱 Browse Phones"],
        ar: ["🛡️ تفاصيل الضمان 12 شهراً", "💳 طرق الدفع الآمنة", "📱 تصفح الهواتف"]
      },
      showCards: false
    },
    {
      id: "shipping",
      keywords: ["shipping", "delivery", "arrive", "time", "fast", "how long", "aramex", "smsa", "توصيل", "شحن", "كم ياخذ", "متى يوصل", "ارامكس", "سمسا"],
      answer: {
        en: "**Delivery**\n\nDelivery options and any shipping charge depend on your address. Enter your address at checkout to see the available method and final total.",
        ar: "**التوصيل**\n\nتعتمد خيارات التوصيل ورسومه على عنوانك. أدخل عنوانك عند إتمام الطلب لمعرفة الطريقة المتاحة والإجمالي النهائي."
      },
      pills: {
        en: ["💳 Payment Methods", "🛡️ 12-Month Warranty", "📱 Browse in-stock tech"],
        ar: ["💳 خيارات الدفع", "🛡️ ضمان 12 شهراً", "📱 تصفح الأجهزة"]
      },
      showCards: false
    },
    {
      id: "box_contents",
      keywords: ["in the box", "accessories", "charger", "cable", "box", "علبة", "محتويات", "شاحن", "كيبل", "كرتون", "ايش يجي معه", "اغراض"],
      answer: {
        en: "**What's included**\n\nCheck the individual product description for the items included with that device. If packaging or accessories are important to you, confirm with support before ordering.",
        ar: "**محتويات الطلب**\n\nراجع وصف الجهاز لمعرفة الملحقات المرفقة به. إذا كان التغليف أو أحد الملحقات مهماً لك، فتأكد من الدعم قبل الطلب."
      },
      pills: {
        en: ["🔋 Battery details", "🛡️ Warranty details", "📱 View Phones"],
        ar: ["🔋 تفاصيل البطارية", "🛡️ تفاصيل الضمان", "📱 تصفح الهواتف"]
      },
      showCards: false
    }
  ];

  function queryLocalKnowledgeEngine(userText, catalog, budgetConstraint, categoryIntent) {
    const textLower = userText.toLowerCase();

    // 1. Check FAQ & Policy Knowledge Base by Best Keyword Match Count & Priority
    let bestFaqMatch = null;
    let maxMatches = 0;

    for (const item of FAQ_KNOWLEDGE_BASE) {
      let matches = 0;
      for (const kw of item.keywords) {
        if (textLower.includes(kw.toLowerCase())) {
          matches += kw.length > 3 ? 2 : 1;
        }
      }
      if (matches > maxMatches) {
        maxMatches = matches;
        bestFaqMatch = item;
      }
    }

    if (bestFaqMatch && maxMatches > 0) {
      const showProducts = bestFaqMatch.showCards !== false && catalog && catalog.length > 0;
      const suggestedCards = showProducts ? catalog.slice(0, 3) : [];
      return {
        reply: isArabic ? bestFaqMatch.answer.ar : bestFaqMatch.answer.en,
        matchedProducts: suggestedCards,
        followUpPills: (isArabic ? bestFaqMatch.pills.ar : bestFaqMatch.pills.en).map(p => ({ label: p, value: p })),
        showProducts: suggestedCards.length > 0
      };
    }

    // 2. Intelligent Catalog Semantic Search with Model & Token Boosting
    let matchedProducts = [];
    if (catalog && catalog.length > 0) {
      const stopWords = new Set(['do', 'you', 'have', 'i', 'want', 'need', 'looking', 'for', 'the', 'a', 'in', 'stock', 'phone', 'phones', 'device', 'هل', 'عندكم', 'ابي', 'اريد', 'جوال']);
      const searchTokens = textLower
        .replace(/[^\w\s\u0600-\u06FF]/g, ' ')
        .split(/\s+/)
        .filter(t => t.length > 0 && !stopWords.has(t));

      // Filter by category and budget if detected
      let candidates = catalog.filter(p => {
        if (categoryIntent && p.category !== categoryIntent) return false;
        if (p.price < budgetConstraint.min || p.price > budgetConstraint.max) return false;
        return true;
      });

      // Score candidates with heavy model-number weighting
      const scored = candidates.map(p => {
        let s = 0;
        const titleLower = (p.title || '').toLowerCase();
        const handleLower = (p.handle || '').toLowerCase();

        for (let tok of searchTokens) {
          // Model numbers (15, 14, 13, 12, 11, m1, m2)
          if (/^[0-9]{1,2}$/.test(tok) || /^[mM][1-3]$/.test(tok)) {
            const regex = new RegExp(`\\b${tok}\\b`, 'i');
            if (regex.test(titleLower)) {
              s += 40;
            } else {
              s -= 15;
            }
            continue;
          }

          // Modifiers (pro, max, plus, mini, air)
          if (['pro', 'max', 'plus', 'mini', 'air'].includes(tok)) {
            if (titleLower.includes(tok)) s += 25;
            continue;
          }

          if (titleLower.includes(tok)) s += 15;
          if (handleLower.includes(tok)) s += 10;
        }
        return { product: p, score: s };
      });

      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return b.product.price - a.product.price;
      });

      matchedProducts = scored.filter(item => item.score > 0).map(item => item.product).slice(0, 4);

      if (matchedProducts.length === 0) {
        let backup = catalog.filter(p => !categoryIntent || p.category === categoryIntent);
        backup.sort((a, b) => Math.abs(a.price - budgetConstraint.max) - Math.abs(b.price - budgetConstraint.max));
        matchedProducts = backup.slice(0, 3);
      }
    }

    let reply = "";
    const budgetStr = budgetConstraint.max < 90000 
      ? ` (${budgetConstraint.max} ${currencySymbol})` 
      : "";

    if (isArabic) {
      if (matchedProducts.length > 0) {
        reply = `✨ **هذه أجهزة متوفرة تناسب طلبك${budgetStr}:**\n\nراجع تفاصيل الحالة والبطارية والضمان في صفحة كل جهاز، ثم تأكد من الإجمالي وخيارات التوصيل عند إتمام الطلب:`;
      } else {
        reply = `اسألني عن جهاز أو حدد ميزانيتك، وسأعرض لك الخيارات المتوفرة.`;
      }
    } else {
      if (matchedProducts.length > 0) {
        reply = `✨ **Here are available devices matching your request${budgetStr}:**\n\nCheck each product page for condition, battery and warranty details, then confirm the delivery options and total at checkout:`;
      } else {
        reply = `Ask about a device or tell me your budget, and I'll show available options.`;
      }
    }

    const pills = isArabic 
      ? [`هاتف أقل من 1500 ${currencySymbol}`, "ماك بوك للدراسة والعمل", "تفاصيل الضمان", "صحة البطارية"]
      : ["📱 iPhone under 1500 " + currencyCode, "💻 MacBook for Study/Work", "🛡️ Warranty Policy", "🔋 Battery Guarantee"];

    return {
      reply: reply,
      matchedProducts: matchedProducts,
      followUpPills: pills.map(p => ({ label: p, value: p })),
      showProducts: matchedProducts.length > 0
    };
  }

  // Main Handler for user messages
  async function handleUserMessage(userText) {
    addUserMessage(userText);
    state.chatHistory.push({ role: 'user', content: userText });

    // A greeting is a conversation opener, not a product search.
    if (/^(?:hi|hello|hey|hiya|good\s+(?:morning|afternoon|evening)|مرحبا|مرحباً|اهلا|أهلا|السلام عليكم)[!.،\s]*$/iu.test(userText)) {
      const reply = isArabic ? "أهلاً! ما الجهاز الذي تبحث عنه، وما ميزانيتك؟" : "Hi! What device are you looking for, and what is your budget?";
      state.chatHistory.push({ role: "assistant", content: reply });
      addBotMessage(reply);
      return;
    }

    showTypingIndicator();
    await loadCatalog();

    const budgetConstraint = parseBudgetConstraints(userText);
    const categoryIntent = parseCategoryIntent(userText);

    const policyTopic = FAQ_KNOWLEDGE_BASE.find(item =>
      ['payments', 'returns', 'shipping', 'warranty', 'battery', 'box_contents'].includes(item.id) &&
      item.keywords.some(keyword => userText.toLowerCase().includes(keyword.toLowerCase()))
    );
    const aiResponse = policyTopic ? null : await callBackendServer(userText);

    removeTypingIndicator();

    let matchedProducts = [];
    let replyText = '';
    let followUpPills = [];
    let showProducts = false;

    if (policyTopic) {
      replyText = isArabic ? policyTopic.answer.ar : policyTopic.answer.en;
      followUpPills = (isArabic ? policyTopic.pills.ar : policyTopic.pills.en).map(label => ({ label, value: label }));
    } else if (aiResponse && aiResponse.reply) {
      replyText = aiResponse.reply;
      followUpPills = (aiResponse.follow_up_pills || []).map(p => ({ label: p, value: p }));
      showProducts = aiResponse.show_products === true || (Array.isArray(aiResponse.recommended_handles) && aiResponse.recommended_handles.length > 0);

      if (Array.isArray(aiResponse.recommended_handles) && aiResponse.recommended_handles.length > 0) {
        for (let h of aiResponse.recommended_handles) {
          const found = state.catalog.find(p => p.handle === h || p.handle.includes(h) || (p.title && p.title.toLowerCase().includes(h.toLowerCase())));
          if (found && !matchedProducts.some(m => m.id === found.id)) {
            matchedProducts.push(found);
          }
        }
      }

      // Check if catalog products are mentioned directly in the AI's response text
      if (matchedProducts.length === 0 && replyText && state.catalog.length > 0) {
        const lowerReply = replyText.toLowerCase();
        for (let p of state.catalog) {
          if (p.title && lowerReply.includes(p.title.toLowerCase()) && !matchedProducts.some(m => m.id === p.id)) {
            matchedProducts.push(p);
            if (matchedProducts.length >= 3) break;
          }
        }
      }
    } else {
      // Local catalog and policy fallback
      const localResult = queryLocalKnowledgeEngine(userText, state.catalog, budgetConstraint, categoryIntent);
      replyText = localResult.reply;
      matchedProducts = localResult.matchedProducts || [];
      followUpPills = localResult.followUpPills || [];
      showProducts = localResult.showProducts || matchedProducts.length > 0;
    }

    if (showProducts && matchedProducts.length === 0 && state.catalog.length > 0) {
      let candidates = state.catalog.filter(p => {
        if (categoryIntent && p.category !== categoryIntent) return false;
        if (p.price < budgetConstraint.min || p.price > budgetConstraint.max) return false;
        return true;
      });

      candidates.sort((a, b) => a.price - b.price);
      matchedProducts = candidates.slice(0, 4);
    }

    if (!replyText) {
      replyText = isArabic
        ? "اسأل عن جهاز أو حدد ميزانيتك وسأعرض لك الخيارات المتوفرة."
        : "Ask about a device or your budget and I'll show available options.";
    }

    state.chatHistory.push({ role: 'assistant', content: replyText });

    // Follow-up chips duplicate the text input and restart control.
    addBotMessage(replyText, null, matchedProducts);
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
